"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";

export type QuickEditArtwork = {
  id: string;
  title: string;
  fileName: string;
  fileType: string;
  downloadUrl?: string;
  previewUrl?: string;
  thumbnailUrl?: string;
};

export interface OpenEditorOptions {
  autoApplyBrandColors?: boolean;
}

interface QuickEditorContextValue {
  isOpen: boolean;
  artwork: QuickEditArtwork | null;
  openEditor: (artwork: QuickEditArtwork, options?: OpenEditorOptions) => void;
  closeEditor: () => void;
  updateArtwork: (updates: Partial<QuickEditArtwork>) => void;
}

const QuickEditorContext = createContext<QuickEditorContextValue | undefined>(undefined);

export function QuickEditorProvider({ children }: { readonly children: ReactNode }) {
  const router = useRouter();
  const [artwork, setArtwork] = useState<QuickEditArtwork | null>(null);

  const buildEditorUrl = useCallback((target: QuickEditArtwork, options?: OpenEditorOptions) => {
    const params = new URLSearchParams();
    const safeTitle = target.title || "Projeto sem título";
    const safeFileName = target.fileName || `${safeTitle}.psd`;
    params.set("title", safeTitle);
    params.set("fileName", safeFileName);
    params.set("fileType", target.fileType || "psd");

    if (target.downloadUrl) params.set("downloadUrl", target.downloadUrl);
    if (target.previewUrl) params.set("previewUrl", target.previewUrl);
    if (target.thumbnailUrl) params.set("thumb", target.thumbnailUrl);
    
    // Auto-apply brand colors option
    if (options?.autoApplyBrandColors) params.set("applyBrand", "1");

    if (typeof window !== "undefined") {
      const from = window.location.pathname + window.location.search;
      if (from) {
        params.set("from", from);
      }
    }

    return `/editor/one-click/${target.id}?${params.toString()}`;
  }, []);

  const openEditor = useCallback((nextArtwork: QuickEditArtwork, options?: OpenEditorOptions) => {
    // console.log('🎯 QuickEditorContext.openEditor navigate to workspace:', {
      // id: nextArtwork.id,
      // title: nextArtwork.title,
      // fileName: nextArtwork.fileName,
      // downloadUrl: nextArtwork.downloadUrl,
      // autoApplyBrandColors: options?.autoApplyBrandColors
    // });
    setArtwork(nextArtwork);
    const url = buildEditorUrl(nextArtwork, options);
    router.push(url);
  }, [buildEditorUrl, router]);

  const closeEditor = useCallback(() => {
    setArtwork(null);
    router.back();
  }, [router]);

  const updateArtwork = useCallback((updates: Partial<QuickEditArtwork>) => {
    setArtwork((current) => (current ? { ...current, ...updates } : current));
  }, []);

  const value = useMemo(
    () => ({
      isOpen: Boolean(artwork),
      artwork,
      openEditor,
      closeEditor,
      updateArtwork
    }),
    [artwork, openEditor, closeEditor, updateArtwork]
  );

  return <QuickEditorContext.Provider value={value}>{children}</QuickEditorContext.Provider>;
}

export function useQuickEditor() {
  const context = useContext(QuickEditorContext);
  if (!context) {
    throw new Error("useQuickEditor must be used within QuickEditorProvider");
  }
  return context;
}
